import { useTabelasPreco } from '@/api/tabelas-preco';
import { SeletorCategoria } from '@/components/categorias/seletor-categoria';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ORIGENS_MARGENS, ROTULO_ORIGEM, ROTULO_SITUACAO, ROTULO_TIPO, temFiltro, TIPOS, type BuscaMargens, type OrigemMargens, type SituacaoMargens, type TipoItem } from './tipos';

const TODAS = '__todas__';
const TRIGGER = 'h-[38px] min-w-[150px] rounded-[10px] bg-card text-small';
const ROTULO = 'text-caption font-medium text-ink-secondary';
const DATA = 'h-[38px] rounded-[10px] border border-input bg-card px-2.5 text-small outline-none focus-visible:border-primary focus-visible:shadow-focus';
const SELECT_NATIVO = `${DATA} min-w-[170px]`;

export interface FiltrosMargensProps {
  busca: BuscaMargens;
  onBusca: (mudanca: Partial<Record<keyof BuscaMargens, string | undefined>>) => void;
  onLimpar: () => void;
}

/** Periodo, tabela, tipo e situacao. Nada filtra no front: muda a URL e a API agrega de novo. */
export function FiltrosMargens({ busca, onBusca, onLimpar }: FiltrosMargensProps) {
  const tabelas = useTabelasPreco({ limit: 200 });
  return (
    <div role="group" aria-label="Filtros do painel" className="flex flex-wrap items-end gap-3">
      <div className="grid gap-1.5">
        <label htmlFor="mg-de" className={ROTULO}>De</label>
        <input id="mg-de" type="date" value={busca.dataDe ?? ''} max={busca.dataAte} onChange={(e) => onBusca({ dataDe: e.target.value || undefined })} className={DATA} />
      </div>
      <div className="grid gap-1.5">
        <label htmlFor="mg-ate" className={ROTULO}>Até</label>
        <input id="mg-ate" type="date" value={busca.dataAte ?? ''} min={busca.dataDe} onChange={(e) => onBusca({ dataAte: e.target.value || undefined })} className={DATA} />
      </div>
      <div className="grid gap-1.5">
        <label htmlFor="mg-dataref" className={ROTULO}>Data do período</label>
        <select
          id="mg-dataref"
          value={busca.dataRef ?? 'ORCAMENTO'}
          onChange={(e) => onBusca({ dataRef: e.target.value === 'APROVACAO' ? 'APROVACAO' : undefined })}
          aria-describedby={busca.dataRef === 'APROVACAO' ? 'mg-dataref-ajuda' : undefined}
          className={SELECT_NATIVO}
        >
          <option value="ORCAMENTO">Do orçamento (padrão)</option>
          <option value="APROVACAO">Da aprovação</option>
        </select>
        {busca.dataRef === 'APROVACAO' && (
          <p id="mg-dataref-ajuda" className="m-0 max-w-[280px] text-caption text-ink-muted">
            Só orçamentos aprovados têm data de aprovação; os demais ficam fora do período.
          </p>
        )}
      </div>
      <div className="grid gap-1.5">
        <label htmlFor="mg-tabela" className={ROTULO}>Tabela de preço</label>
        <Select value={busca.tabelaId ?? TODAS} onValueChange={(v) => onBusca({ tabelaId: v === TODAS ? undefined : v })}>
          <SelectTrigger id="mg-tabela" className={TRIGGER}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODAS}>Todas as tabelas</SelectItem>
            {(tabelas.data ?? []).map((t) => (
              <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-1.5">
        <label htmlFor="mg-tipo" className={ROTULO}>Tipo de item</label>
        <Select value={busca.tipo ?? TODAS} onValueChange={(v) => onBusca({ tipo: v === TODAS ? undefined : (v as TipoItem) })}>
          <SelectTrigger id="mg-tipo" className={TRIGGER}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODAS}>Todos os tipos</SelectItem>
            {TIPOS.map((t) => (
              <SelectItem key={t} value={t}>{ROTULO_TIPO[t]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-1.5">
        <SeletorCategoria id="mg-categoria" rotulo="Categoria" comTodas value={busca.categoriaId} onChange={(v) => onBusca({ categoriaId: v })} className={`${TRIGGER} w-auto`} />
      </div>
      <div className="grid gap-1.5">
        <label htmlFor="mg-status" className={ROTULO}>Situação</label>
        <Select value={busca.escopo ?? TODAS} onValueChange={(v) => onBusca({ escopo: v === TODAS ? undefined : (v as SituacaoMargens) })}>
          <SelectTrigger id="mg-status" className={TRIGGER}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODAS}>Aprovados e rascunhos</SelectItem>
            {(Object.keys(ROTULO_SITUACAO) as SituacaoMargens[]).map((s) => (
              <SelectItem key={s} value={s}>{ROTULO_SITUACAO[s]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-1.5">
        <label htmlFor="mg-origem" className={ROTULO}>Origem</label>
        <Select value={busca.origemMargens ?? 'APP'} onValueChange={(v) => onBusca({ origemMargens: v === 'APP' ? undefined : (v as OrigemMargens) })}>
          <SelectTrigger id="mg-origem" className={TRIGGER}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ORIGENS_MARGENS.map((o) => (
              <SelectItem key={o} value={o}>{ROTULO_ORIGEM[o]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {temFiltro(busca) && (
        <button type="button" onClick={onLimpar} className="h-[38px] rounded-[10px] border border-border bg-card px-3 text-small hover:bg-line-soft">
          Limpar filtros
        </button>
      )}
    </div>
  );
}
